import { useEffect, useRef, useState } from 'react';

// Botón + visor de la guía para crear el acceso directo del sistema en el
// celular (imagen estática public/guia_celular.png). El visor es un modal a
// pantalla completa: la imagen ocupa el ancho disponible y se desplaza
// verticalmente si es más alta que la pantalla. Se cierra con la X, tocando
// fuera de la imagen o con Esc.
const IMAGEN = '/guia_celular.png';

// En celular algunas pantallas del sistema son más anchas que el dispositivo;
// el navegador agranda entonces el área de position:fixed y un modal con
// inset:0 queda más ancho que lo visible (la X se sale por la derecha). Por
// eso el visor se ajusta al área realmente visible (visualViewport).
function areaVisible() {
  const vv = window.visualViewport;
  if (!vv) return { left: 0, top: 0, width: '100%', height: '100%' };
  return { left: vv.offsetLeft, top: vv.offsetTop, width: vv.width, height: vv.height };
}

const st = {
  boton: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
    whiteSpace: 'normal', overflowWrap: 'break-word',
    width: '100%', maxWidth: 640, minHeight: 48, marginBottom: 16, padding: '12px 20px',
    backgroundColor: '#1a3a6e', color: 'white', border: 'none', borderRadius: 6,
    fontSize: 15, fontWeight: '600', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    boxSizing: 'border-box', WebkitTapHighlightColor: 'transparent',
  },
  marco: { position: 'fixed', zIndex: 2000, backgroundColor: 'rgba(0,0,0,0.85)' },
  desplazable: {
    position: 'absolute', inset: 0, overflowY: 'auto', overflowX: 'hidden',
    overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch',
    padding: '64px 12px 24px', boxSizing: 'border-box',
  },
  imagen: {
    display: 'block', width: '100%', maxWidth: 540, height: 'auto', margin: '0 auto',
    borderRadius: 8, boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
  },
  cerrar: {
    position: 'absolute', top: 'max(10px, env(safe-area-inset-top))', right: 10, zIndex: 1,
    width: 48, height: 48, borderRadius: '50%', border: '2px solid white',
    backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', fontSize: 28, lineHeight: '44px',
    padding: 0, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  },
};

export default function GuiaAccesoCelular() {
  const [abierta, setAbierta] = useState(false);
  const [area, setArea] = useState(null);
  const botonCerrar = useRef(null);

  useEffect(() => {
    if (!abierta) return undefined;
    const alTeclear = (e) => { if (e.key === 'Escape') setAbierta(false); };
    const ajustar = () => setArea(areaVisible());
    ajustar();
    document.addEventListener('keydown', alTeclear);
    window.visualViewport?.addEventListener('resize', ajustar);
    window.visualViewport?.addEventListener('scroll', ajustar);
    // Evita que la página de atrás se desplace mientras el visor está abierto.
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    botonCerrar.current?.focus();
    return () => {
      document.removeEventListener('keydown', alTeclear);
      window.visualViewport?.removeEventListener('resize', ajustar);
      window.visualViewport?.removeEventListener('scroll', ajustar);
      document.body.style.overflow = overflowPrevio;
    };
  }, [abierta]);

  return (
    <>
      <button type="button" style={st.boton} onClick={() => setAbierta(true)}>
        Instrucciones para aplicación en tu celular
      </button>
      {abierta && (
        <div
          style={{ ...st.marco, ...(area || areaVisible()) }}
          role="dialog"
          aria-modal="true"
          aria-label="Guía para crear el acceso directo en tu celular"
        >
          <div style={st.desplazable} onClick={() => setAbierta(false)}>
            <img
              src={IMAGEN}
              alt="Guía para crear el acceso directo del sistema en tu celular"
              style={st.imagen}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <button ref={botonCerrar} type="button" style={st.cerrar} aria-label="Cerrar" onClick={() => setAbierta(false)}>
            ×
          </button>
        </div>
      )}
    </>
  );
}
